import { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { BarChart3, PiggyBank, Sparkles, TrendingUp } from 'lucide-react';
import {
  ActionButton,
  ContextHero,
  DataCard,
  DateBadge,
  EmptyStateModern,
  KpiCard,
  MoneyAmount,
  PageContainer,
  SectionTitle,
  SkeletonShimmer,
} from '@/components/design-system';
import { BenefitSimulatorCard } from '@/components/design-system/BenefitSimulatorCard';
import { useFormationB2CKpis, useFormationB2CRoi } from '@/hooks/queries/useFormationB2C';

export function B2cRoiTab(): JSX.Element {
  const { data: kpis } = useFormationB2CKpis();
  const { data: roi = [], isLoading } = useFormationB2CRoi();
  const [mode, setMode] = useState<'liste' | 'simulateur'>('liste');

  // Simulateur autonome
  const [prixUnit, setPrixUnit] = useState(2500);
  const [nbPart, setNbPart] = useState(10);
  const [paiementType, setPaiementType] = useState<'forfait_fixe' | 'pourcentage_ca'>(
    'forfait_fixe',
  );
  const [forfait, setForfait] = useState(15000);
  const [pctCa, setPctCa] = useState(40);
  const [chargesAutres, setChargesAutres] = useState(0);

  const { caSimu, formateurSimu, beneficeSimu, simuCharges } = useMemo(() => {
    const ca = prixUnit * nbPart;
    const formateur = paiementType === 'forfait_fixe' ? forfait : (ca * pctCa) / 100;
    return {
      caSimu: ca,
      formateurSimu: formateur,
      beneficeSimu: ca - formateur - chargesAutres,
      simuCharges: [
        { categorie: 'honoraires_formateur', total: formateur },
        { categorie: 'autre', total: chargesAutres },
      ],
    };
  }, [prixUnit, nbPart, paiementType, forfait, pctCa, chargesAutres]);

  const benefice30j = roi
    .filter((r) => new Date(r.date_action) >= new Date(Date.now() - 30 * 86400000))
    .reduce((s, r) => s + Number(r.benefice), 0);
  const margeMoyenne = (() => {
    const eligible = roi.filter((r) => Number(r.recettes) > 0);
    if (eligible.length === 0) return 0;
    return (
      eligible.reduce((s, r) => s + (Number(r.benefice) / Number(r.recettes)) * 100, 0) /
      eligible.length
    );
  })();
  const top = roi.slice().sort((a, b) => Number(b.benefice) - Number(a.benefice))[0];
  const flop = roi.slice().sort((a, b) => Number(a.benefice) - Number(b.benefice))[0];

  return (
    <PageContainer>
      <div className="space-y-6">
        <ContextHero
          variant="formation_b2c"
          icon={<BarChart3 className="h-5 w-5" />}
          title="ROI & Simulateur B2C"
          subtitle="Bénéfice par séance hebdomadaire et simulateur de rentabilité."
          actions={
            <div className="flex overflow-hidden rounded-xl bg-white ring-1 ring-slate-200">
              <button
                onClick={() => setMode('liste')}
                className={
                  'px-3 py-1.5 text-xs font-semibold transition-colors ' +
                  (mode === 'liste' ? 'bg-rose-500 text-white' : 'text-slate-600 hover:bg-slate-50')
                }
              >
                Liste actions
              </button>
              <button
                onClick={() => setMode('simulateur')}
                className={
                  'px-3 py-1.5 text-xs font-semibold transition-colors ' +
                  (mode === 'simulateur'
                    ? 'bg-rose-500 text-white'
                    : 'text-slate-600 hover:bg-slate-50')
                }
              >
                Simulateur
              </button>
            </div>
          }
        />

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <KpiCard
            variant="formation_b2c"
            label="Bénéfice total 30j"
            value={<MoneyAmount amount={benefice30j} variant="inline" withColor />}
            icon={<PiggyBank className="h-4 w-4" />}
            loading={isLoading}
          />
          <KpiCard
            variant="formation_b2c"
            label="Marge moyenne"
            value={`${margeMoyenne.toFixed(1)}%`}
            icon={<Sparkles className="h-4 w-4" />}
            loading={isLoading}
          />
          <KpiCard
            variant="formation_b2c"
            label="Top action"
            hint={top?.formation_titre ?? '—'}
            value={top ? <MoneyAmount amount={top.benefice} variant="inline" /> : '—'}
            loading={isLoading}
          />
          <KpiCard
            variant="formation_b2c"
            label="Action moins rentable"
            hint={flop?.formation_titre ?? '—'}
            value={flop ? <MoneyAmount amount={flop.benefice} variant="inline" withColor /> : '—'}
            loading={isLoading}
          />
        </div>

        {mode === 'simulateur' ? (
          <>
            <SectionTitle>Simulateur B2C</SectionTitle>
            <div className="grid gap-5 lg:grid-cols-2">
              <DataCard title="Hypothèses">
                <div className="space-y-3">
                  <label className="block">
                    <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
                      Prix unitaire (DA)
                    </span>
                    <input
                      type="number"
                      value={prixUnit}
                      onChange={(e) => setPrixUnit(Number(e.target.value))}
                      className="input mt-1"
                    />
                  </label>
                  <label className="block">
                    <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
                      Nb participants prévus
                    </span>
                    <input
                      type="number"
                      value={nbPart}
                      onChange={(e) => setNbPart(Number(e.target.value))}
                      className="input mt-1"
                    />
                  </label>
                  <div>
                    <span className="mb-1 block text-[10px] font-bold uppercase tracking-widest text-slate-500">
                      Modèle paiement formateur
                    </span>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => setPaiementType('forfait_fixe')}
                        className={
                          'rounded-xl border-2 px-3 py-2 text-xs font-bold transition-all ' +
                          (paiementType === 'forfait_fixe'
                            ? 'border-rose-500 bg-rose-50 text-rose-700'
                            : 'border-slate-200 bg-white text-slate-600')
                        }
                      >
                        Forfait fixe
                      </button>
                      <button
                        type="button"
                        onClick={() => setPaiementType('pourcentage_ca')}
                        className={
                          'rounded-xl border-2 px-3 py-2 text-xs font-bold transition-all ' +
                          (paiementType === 'pourcentage_ca'
                            ? 'border-rose-500 bg-rose-50 text-rose-700'
                            : 'border-slate-200 bg-white text-slate-600')
                        }
                      >
                        % du CA
                      </button>
                    </div>
                  </div>
                  {paiementType === 'forfait_fixe' ? (
                    <label className="block">
                      <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
                        Forfait (DA)
                      </span>
                      <input
                        type="number"
                        value={forfait}
                        onChange={(e) => setForfait(Number(e.target.value))}
                        className="input mt-1"
                      />
                    </label>
                  ) : (
                    <label className="block">
                      <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
                        % CA
                      </span>
                      <input
                        type="number"
                        step="0.5"
                        value={pctCa}
                        onChange={(e) => setPctCa(Number(e.target.value))}
                        className="input mt-1"
                      />
                    </label>
                  )}
                  <label className="block">
                    <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
                      Charges autres (DA)
                    </span>
                    <input
                      type="number"
                      value={chargesAutres}
                      onChange={(e) => setChargesAutres(Number(e.target.value))}
                      className="input mt-1"
                    />
                  </label>
                  <div className="rounded-xl bg-slate-50 p-3 text-xs">
                    <div className="flex justify-between">
                      <span>CA simulé</span>
                      <MoneyAmount amount={caSimu} variant="inline" />
                    </div>
                    <div className="flex justify-between">
                      <span>Rému formateur</span>
                      <MoneyAmount amount={formateurSimu} variant="inline" />
                    </div>
                    <div className="mt-1 flex justify-between border-t border-slate-200 pt-1 font-bold">
                      <span>Bénéfice</span>
                      <MoneyAmount amount={beneficeSimu} variant="inline" withColor />
                    </div>
                  </div>
                </div>
              </DataCard>

              <BenefitSimulatorCard
                prixVenteHT={caSimu}
                totalCharges={formateurSimu + chargesAutres}
                charges={simuCharges}
                variant="formation_b2c"
              />
            </div>
          </>
        ) : isLoading ? (
          <SkeletonShimmer variant="table" count={5} />
        ) : roi.length === 0 ? (
          <EmptyStateModern
            variant="formation_b2c"
            icon={<BarChart3 className="h-7 w-7" />}
            title="Aucune action B2C avec participants"
            description="Inscrivez des participants à une action pour voir son bénéfice apparaître ici."
            ctaLabel="Lancer le simulateur"
            onCtaClick={() => setMode('simulateur')}
          />
        ) : (
          <DataCard>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-left text-[10px] uppercase tracking-widest text-slate-400">
                  <tr>
                    <th className="py-2">Réf</th>
                    <th className="py-2">Date</th>
                    <th className="py-2">Formation</th>
                    <th className="py-2">Formateur</th>
                    <th className="py-2 text-right">Participants</th>
                    <th className="py-2 text-right">Recettes</th>
                    <th className="py-2 text-right">Rému formateur</th>
                    <th className="py-2 text-right">Bénéfice</th>
                  </tr>
                </thead>
                <tbody>
                  {roi.map((r, i) => (
                    <motion.tr
                      key={r.action_id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: Math.min(i, 8) * 0.02 }}
                      className="border-t border-slate-100 hover:bg-slate-50"
                    >
                      <td className="py-2 font-mono">{r.reference}</td>
                      <td className="py-2">
                        <DateBadge date={r.date_action} />
                      </td>
                      <td className="py-2 font-semibold text-slate-800">
                        {r.formation_titre ?? '—'}
                      </td>
                      <td className="py-2 text-slate-600">{r.formateur_nom_complet ?? '—'}</td>
                      <td className="py-2 text-right tabular-nums">
                        {r.nb_participants} / {r.capacite_max}
                      </td>
                      <td className="py-2 text-right">
                        <MoneyAmount amount={r.recettes} variant="inline" />
                      </td>
                      <td className="py-2 text-right">
                        <MoneyAmount amount={r.remuneration_formateur} variant="inline" />
                      </td>
                      <td className="py-2 text-right">
                        <MoneyAmount amount={r.benefice} variant="inline" withColor />
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          </DataCard>
        )}

        {mode === 'liste' && roi.length > 0 && (
          <div className="flex justify-end">
            <ActionButton
              variant="gradient"
              module="formation_b2c"
              onClick={() => setMode('simulateur')}
            >
              <Sparkles className="h-3.5 w-3.5" /> Simuler une nouvelle action
            </ActionButton>
          </div>
        )}

        <span className="hidden">
          {kpis?.actions_30j ?? ''}
          <TrendingUp className="h-3 w-3" />
        </span>
      </div>
    </PageContainer>
  );
}
